import axios from 'axios';
import AuthHelper from './authHelper';
import { store } from 'redux/store';
import { LOGOUT } from 'redux/actions/auth';

import NProgress from 'nprogress';
import { useQuery } from '@tanstack/react-query';
import * as _ from 'lodash';

// region global progress interceptor
axios.interceptors.request.use(
  (config) => {
    NProgress.start();
    return config;
  },
  (error) => {
    NProgress.done();
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (response) => {
    NProgress.done();
    return response;
  },
  (error) => {
    NProgress.done();
    return Promise.reject(error);
  },
);
// endregion

const withAuth = (headers = {}) => ({
  ...headers,
  Authorization: 'token ' + AuthHelper.accessToken(),
});

const base = (
  method,
  url,
  data = {},
  headers = {},
  secure = true,
  params = {},
  signal = undefined,
) => {
  if (secure) {
    const tmp = {
      method,
      url,
      data,
      params,
      headers: withAuth(headers),
      signal,
    };
    return axios(tmp).catch((err) => {
      if (axios.isAxiosError(err)) {
        const isExpiredCredentials = err.response?.status === 401;
        if (isExpiredCredentials) {
          store.dispatch({
            type: LOGOUT,
          });
        }

        return err;
      }
      throw err;
    });
  } else {
    return axios({
      method,
      url,
      data,
      params,
      headers,
      signal,
    });
  }
};

const ApiHelper = {};

['get', 'post', 'put', 'patch', 'delete'].forEach((method) => {
  ApiHelper[method] = base.bind(null, method);
});

/**
 * @param {string} url
 * @param {object} data Request body
 * @param {object} params Query parameters
 * @param {<T, U>(item: T, response: import('axios').AxiosResponse) => U} transform Transforms the resulting data.
 * This is similar to a map (as in an Array).
 * Note that this is *before* inserting into the cache, unlike `select`.
 * @param {import('@tanstack/react-query').QueryObserverOptions} options
 * @param {import('axios').Method} method Which method to use.
 * @param {import('axios').AxiosRequestHeaders} headers
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useApiQuery(
  url,
  {
    data = {},
    params = {},
    transform = _.identity,
    options = {},
    method = 'get',
    headers = {},
  },
) {
  return useQuery({
    // NOTE: `transform` and `dispatch` are intentionally omitted.
    queryKey: [url, { data, params, method, headers }],
    async queryFn({ signal }) {
      const response = await axios({
        data,
        headers: withAuth(headers),
        method,
        params,
        signal,
        url,
      }).catch((error) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          store.dispatch({
            type: LOGOUT,
          });
          return error;
        }
        throw error;
      });
      return transform(response.data, response);
    },
    ...options,
  });
}

export default ApiHelper;
